import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCheckCircle,
  faBan,
  faCircleExclamation,
  faServer,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

import { UserStatus } from "../../flatbuffers/generated/archive/user-status";

/**
 * What a single user status means and how it is coloured, in ONE place.
 *
 * Two things used to be wrong and are fixed by this file existing:
 *
 *  - The wizard's status column coloured only ACTIVE, NEW and BLOCKED. The
 *    other four fell through to the default grey, so an OFFLINE_ACTIVE user
 *    and a DELETED one were rendered identically.
 *  - Nothing anywhere told an operator what the statuses MEAN. The column
 *    printed the raw enum name and stopped there.
 *
 * `description` is written from the LiftCloud code that actually writes each
 * status, not from the enum name — see `book/src/users/status-lifecycle.md`
 * for the transition map and the source lines behind every row.
 */
export interface UserStatusMeta {
  /** antd `Tag` colour. */
  color: string;
  /**
   * The icon the status tag carries. Lives here rather than in the table
   * component so the Users table, the wizard's Users step and the legend all
   * paint one status the same way — they used to disagree, most visibly on
   * OFFLINE_CREATED, which the table drew blue and the legend gold. The
   * table won: its colours are pinned by e2e/investigate-user-status.spec.ts,
   * and a refactor that unifies two lists has no business changing what an
   * operator sees. The legend moved to match it.
   */
  icon: IconDefinition;
  /** i18n key for the short label on the tag. */
  labelKey: string;
  /** One line an operator can act on. */
  description: string;
  /**
   * Whether any code path in the platform ever writes this status. A status
   * that nothing writes is still a legal enum member and still has to be
   * rendered if a row somehow carries it — but the legend says so plainly
   * rather than inventing a meaning for it.
   */
  reachable: boolean;
}

export const USER_STATUS_META: Record<UserStatus, UserStatusMeta> = {
  [UserStatus.NEW]: {
    color: "warning",
    icon: faCircleExclamation,
    labelKey: "userManagement.newUser",
    description:
      "Created in the cloud database but not yet linked to a sign-in account. A user normally passes through this within one request; a user who stays here means the sign-in account could not be created.",
    reachable: true,
  },
  [UserStatus.ACTIVE]: {
    color: "green",
    icon: faCheckCircle,
    labelKey: "userManagement.active",
    description:
      "Cloud sign-in account exists and is enabled. This is the only status from which a cloud user can log in.",
    reachable: true,
  },
  [UserStatus.OFFLINE_CREATED]: {
    color: "blue",
    icon: faServer,
    labelKey: "general.offlineCreated",
    description:
      "Created on an edge server with a system-generated offline password. The user can sign in offline but has not yet chosen their own password.",
    reachable: true,
  },
  [UserStatus.OFFLINE_SYNCED]: {
    color: "geekblue",
    icon: faServer,
    labelKey: "general.offlineSynced",
    description:
      "Reserved. No code path in the platform writes this status today — a user will not reach it on their own.",
    reachable: false,
  },
  [UserStatus.OFFLINE_ACTIVE]: {
    color: "cyan",
    icon: faServer,
    labelKey: "general.offlineActive",
    description:
      "The offline user has replaced the generated password with their own. This is the settled state for an edge-only account.",
    reachable: true,
  },
  [UserStatus.BLOCKED]: {
    color: "error",
    icon: faBan,
    labelKey: "userManagement.lock",
    description:
      "An administrator blocked the account. The cloud sign-in account is disabled; unblocking restores it to Active.",
    reachable: true,
  },
  [UserStatus.DELETED]: {
    color: "default",
    icon: faTrash,
    labelKey: "general.deleted",
    description:
      "An administrator deleted the account. The record is retained for history and its sign-in link is cleared.",
    reachable: true,
  },
};

/**
 * Every status, in wire order, derived FROM the generated enum rather than
 * hand-listed. Adding a member to `user_status` in the schema therefore adds
 * a legend row automatically — and fails the type-check on USER_STATUS_META
 * until someone writes down what the new member means.
 */
export const ALL_USER_STATUSES: UserStatus[] = Object.values(UserStatus).filter(
  (value): value is UserStatus => typeof value === "number",
);

/** The antd `Tag` colour for a status. */
export const userStatusColor = (status: UserStatus): string =>
  USER_STATUS_META[status]?.color ?? "default";

/** The FontAwesome icon for a status. */
export const userStatusIcon = (status: UserStatus): IconDefinition =>
  USER_STATUS_META[status]?.icon ?? faCircleExclamation;

/** The i18n key for a status's short tag label. */
export const userStatusLabelKey = (status: UserStatus): string =>
  USER_STATUS_META[status]?.labelKey ?? "";
